import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  if (typeof window === "undefined") {
    return "";
  }

  const runtime = window.ScreenworkMapsRuntime || {};
  return typeof runtime.geocoderKey === "string" ? runtime.geocoderKey.trim() : "";
}

function isFiniteNumber(value) {
  return typeof value === "number" && isFinite(value);
}

function cancelledError() {
  const error = new Error("geocoder request cancelled");
  error.cancelled = true;
  return error;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  isCancel(error) {
    return !!error && (error.cancelled === true || this.axios.isCancel(error));
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(feature) {
    if (!feature || typeof feature !== "object") {
      return null;
    }

    const props = feature.properties && typeof feature.properties === "object" ? feature.properties : {};
    const coordinates = feature.geometry && feature.geometry.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      return null;
    }

    // GeoJSON: [lng, lat]
    const lng = Number(coordinates[0]);
    const lat = Number(coordinates[1]);
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const value = key => (typeof props[key] === "string" || typeof props[key] === "number") && String(props[key]).trim() !== "" ? String(props[key]) : null;
    const type = value("type");
    const name = value("name");

    let street = value("street");
    const number = value("housenumber");
    let city = value("city");
    let district = value("district");

    if (type) {
      if (type === "street" && !street) {
        street = name;
      } else if (type === "city" && !city) {
        city = name;
      } else if ((type === "district" || type === "locality") && !district && name && name !== city) {
        district = name;
      }
    } else {
      // Legacy-Fallback fuer Antworten ohne properties.type
      const osmKey = value("osm_key");
      const osmValue = value("osm_value");

      if (osmKey === "highway" && !street) {
        street = name;
      } else if (name && city && name !== city && !district) {
        if (osmKey === "boundary" || (osmKey === "place" && osmValue === "suburb")) {
          district = name;
        }
      } else if (name && !city && osmKey === "place" && ["city", "town", "village"].indexOf(osmValue) !== -1) {
        city = name;
      }
    }

    const geoaddress = {};

    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    if (value("postcode")) {
      geoaddress["zip"] = value("postcode");
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district && district !== city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = value("state");
    geoaddress["country"] = value("country");

    geoaddress["text"] = this._geoaddressText(geoaddress) || name || "";

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(data) {
    return data && Array.isArray(data.features) ? data.features : [];
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (this.axiosRequest !== request) {
        throw cancelledError();
      }

      const results = [];
      this._features(res.data).forEach(feature => {
        const geoaddress = this._geoaddress(feature);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    lat = Number(lat);
    lng = Number(lng);

    if (!isFiniteNumber(lat) || !isFiniteNumber(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = mapsKey();
    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    const params = new URLSearchParams();
    params.append("lat", String(lat));
    params.append("lon", String(lng));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const features = this._features(res.data);
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
      if (!geoaddress) {
        throw new Error("invalid data");
      }

      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return depsloader.load("axios").then(axios => new Geocoder(axios));
}

export { GeocoderFactory };

export default {};
